/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: E-Commerce Showcase Carousel Section - 景深效果轮播区块
 */
import Image from 'next/image';
import React from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';

import DepthCarousel from '../../common/DepthCarousel';
import styles from './index.module.scss';

export interface ShowcaseItem {
  /** 唯一标识 */
  id: string;
  /** 展示图片 URL */
  imageUrl: string;
  /** 图片 alt 文本 */
  imageAlt: string;
}

export interface ShowcaseCarouselSectionProps {
  /** 区块标题 */
  title: string;
  /** 区块副标题 */
  subtitle: string;
  /** 轮播项列表 */
  items: ShowcaseItem[];
  /** CTA 按钮文本 */
  ctaText: string;
  /** CTA 跳转链接 */
  ctaUrl?: string;
  /** 自动轮播间隔（毫秒），默认 5000 */
  autoPlayInterval?: number;
  /** 是否自动播放，默认 true */
  autoPlay?: boolean;
  /** GTM element-name 属性值 */
  elementName?: string;
}

const ShowcaseCarouselSection: React.FC<ShowcaseCarouselSectionProps> = ({
  title,
  subtitle,
  items,
  ctaText,
  ctaUrl = '/ai-video-generator',
  autoPlayInterval = 5000,
  autoPlay = true,
  elementName,
}) => {
  // 渲染轮播项
  const renderItem = (item: ShowcaseItem, _index: number, isActive: boolean) => (
    <div className={`${styles.imageWrapper} ${isActive ? styles.active : ''}`}>
      <Image
        src={item.imageUrl}
        alt={item.imageAlt}
        fill
        className={styles.image}
        sizes="(max-width: 768px) 280px, (max-width: 1024px) 480px, 680px"
        priority={isActive}
      />
    </div>
  );

  return (
    <section className={styles.section}>
      {/* 标题区域 */}
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
      </div>

      {/* 轮播区域 - 全屏宽度 */}
      <DepthCarousel
        items={items}
        renderItem={renderItem}
        autoPlayInterval={autoPlayInterval}
        autoPlay={autoPlay}
        itemWidth={680}
        itemHeight={400}
        itemGap={24}
      />

      {/* CTA 按钮 */}
      <div className={styles.ctaWrapper}>
        <StartButton
          text={ctaText}
          href={ctaUrl}
          className={styles.ctaButton}
          elementName={elementName}
        />
      </div>
    </section>
  );
};

export default ShowcaseCarouselSection;
